import DOMPurify, { type Config } from 'dompurify';

const domPurify = DOMPurify();

// Keep `style` on incoming HTML but restrict it to declarations the rich-text
// surface relies on: Color (text colour) and Highlight (background-color) marks
// serialise to inline style, and the Image extension's resize handle writes
// inline width/height on <img>. Without this, paste round-trips lose those.
domPurify.addHook('uponSanitizeAttribute', (node, data) => {
  if (data.attrName === 'style') {
    const safe = data.attrValue
      .split(';')
      .map((decl) => decl.trim())
      .filter((decl) => /^(color|background-color|width|height)\s*:/i.test(decl))
      .join('; ');

    if (safe) {
      data.attrValue = safe;
    } else {
      data.keepAttr = false;
    }
    return;
  }

  // Allow `class="emoji"` only on <span>. The Emoji node parses this exact pair as
  // its fallback hook after the backend strips `data-*` attributes. Any other class
  // value (or any other tag carrying class="emoji") still falls through to FORBID_ATTR
  // and gets dropped, so this does not loosen sanitisation for arbitrary elements.
  if (data.attrName === 'class') {
    const tagName = (node as Element).tagName?.toLowerCase();
    if (tagName === 'span' && data.attrValue === 'emoji') {
      data.forceKeepAttr = true;
    }
  }
});

const PASTE_CONFIG: Config = {
  USE_PROFILES: { html: true },
  // Drop presentational/identity carriers. Tiptap re-renders structure with its own
  // classes; incoming class/id just adds noise and round-trip risk. `style` is handled
  // by the hook above so colour/highlight marks survive; `class="emoji"` on <span> is
  // re-allowed by the same hook so the Emoji node can re-parse pasted emoji spans.
  FORBID_ATTR: ['class', 'id'],
  // <font> is deprecated HTML; <style> would otherwise leak CSS rules as visible text.
  FORBID_TAGS: ['style', 'font'],
};

export const sanitizePastedHTML = (html: string): string => {
  if (!html) {
    return html;
  }

  return domPurify.sanitize(html, PASTE_CONFIG) as string;
};
